import React, { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { useTranslation } from "react-i18next";

function normalizeAsset(p) {
  if (!p) return "/assets/placeholder.png";
  if (/^https?:\/\//i.test(p)) return p;
  if (p.startsWith("/")) return p;
  return p.replace(/^(\.\.\/)+assets\//, "/assets/");
}

export default function Baner() {
  const { t } = useTranslation();
  const location = useLocation();

  // زبان از سگمنت اول URL
  const seg = location.pathname.split("/")[1];
  const lang = seg === "fa" || seg === "en" ? seg : "en";
  const isRTL = lang === "fa";

  // اسلایدها: متن‌ها از i18n و مسیر تصاویر نرمال
  const slides = useMemo(
    () => [
      {
        main: normalizeAsset("../../assets/baner2.jpg"),
        overlays: [
          {
            src: normalizeAsset("../../assets/T2000baner.png"),
            className: "onbanerimg-1",
            alt: t("banner.slide1.leftAlt"),
          },
          {
            src: normalizeAsset("../../assets/arrow.png"),
            className: "arrow-image",
            alt: t("banner.slide1.arrowAlt"),
          },
          {
            src: normalizeAsset("../../assets/T3000baner.png"),
            className: "onbanerimg-3",
            alt: t("banner.slide1.rightAlt"),
          },
        ],
        title: t("banner.slide1.title"),
        description: t("banner.slide1.desc"),
      },
      {
        main: normalizeAsset("../../assets/baner3.jpg"),
        overlays: [
          {
            src: normalizeAsset("../../assets/infi90.png"),
            className: "onbanerimg-1",
            alt: t("banner.slide2.leftAlt"),
          },
          {
            src: normalizeAsset("../../assets/arrow.png"),
            className: "arrow-image",
            alt: t("banner.slide2.arrowAlt"),
          },
          {
            src: normalizeAsset("../../assets/splus.png"),
            className: "onbanerimg-3",
            alt: t("banner.slide2.rightAlt"),
          },
        ],
        title: t("banner.slide2.title"),
        description: t("banner.slide2.desc"),
      },
      {
        main: normalizeAsset("../../assets/baner.jpg"),
        overlays: [
          {
            src: normalizeAsset("../../assets/475.jpg"),
            className: "monitoring-image-1",
            alt: t("banner.slide3.leftAlt"),
          },
          {
            src: normalizeAsset("../../assets/Untitled423.jpg"),
            className: "monitoring-image-2",
            alt: t("banner.slide3.centerAlt"),
          },
          {
            src: normalizeAsset("../../assets/Untitled123.jpg"),
            className: "monitoring-image-3",
            alt: t("banner.slide3.rightAlt"),
          },
        ],
        title: t("banner.slide3.title"),
        description: t("banner.slide3.desc"),
      },
    ],
    [t]
  );

  const [currentIndex, setCurrentIndex] = useState(0);
  const [showOverlays, setShowOverlays] = useState(false);

  const nextSlide = () => setCurrentIndex((p) => (p + 1) % slides.length);
  const prevSlide = () =>
    setCurrentIndex((p) => (p - 1 + slides.length) % slides.length);

  // اتوپلی
  useEffect(() => {
    const id = setInterval(() => setCurrentIndex((previous) => (previous + 1) % slides.length), 6000);
    return () => clearInterval(id);
    // عمداً وابسته به slides.length نیست تا هر تغییر زبان تایمر ریست نشه
  }, [slides.length]);

  // زمان‌بندی ظاهر شدن overlayها
  useEffect(() => {
    setShowOverlays(false);
    const to = setTimeout(() => setShowOverlays(true), 1000);
    return () => clearTimeout(to);
  }, [currentIndex]);

  const currentSlide = slides[currentIndex];

  // جای دکمه‌ها مطابق جهت صفحه
  const navLeftStyle = isRTL
    ? { right: 20, left: "auto" }
    : { left: 20, right: "auto" };
  const navRightStyle = isRTL
    ? { left: 20, right: "auto" }
    : { right: 20, left: "auto" };
  const LeftIcon = isRTL ? FaChevronRight : FaChevronLeft;
  const RightIcon = isRTL ? FaChevronLeft : FaChevronRight;

  return (
    <div
      className="home-hero"
      dir={isRTL ? "rtl" : "ltr"}
      style={{
        position: "relative",
        width: "100%",
        height: "80vh",
        overflow: "hidden",
        borderRadius: 0,
        margin: "0 auto",
      }}
      aria-label={t("banner.aria")}
    >
      {/* دکمه قبلی */}
      <motion.button
        onClick={prevSlide}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        aria-label={t("banner.prev")}
        style={{
          position: "absolute",
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 4,
          background: "rgba(255,255,255,0.2)",
          border: "none",
          borderRadius: "50%",
          width: 50,
          height: 50,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          backdropFilter: "blur(5px)",
          ...navLeftStyle,
        }}
      >
        <LeftIcon size={24} color="white" />
      </motion.button>

      {/* دکمه بعدی */}
      <motion.button
        onClick={nextSlide}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        aria-label={t("banner.next")}
        style={{
          position: "absolute",
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 4,
          background: "rgba(255,255,255,0.2)",
          border: "none",
          borderRadius: "50%",
          width: 50,
          height: 50,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          backdropFilter: "blur(5px)",
          ...navRightStyle,
        }}
      >
        <RightIcon size={24} color="white" />
      </motion.button>

      {/* تصویر پس‌زمینه (تزئینی) */}
      <AnimatePresence mode="wait">
        <motion.img
          key={currentSlide.main}
          src={currentSlide.main}
          alt=""
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1 }}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            position: "absolute",
            top: 0,
            left: 0,
            zIndex: 1,
          }}
        />
      </AnimatePresence>

      {/* متن روی تصویر */}
      <motion.div
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1 }}
        style={{
          position: "absolute",
          top: "10%",
          left: 0,
          right: 0,
          margin: "0 auto",
          zIndex: 3,
          color: "#fff",
          textAlign: "center",
          textShadow: "0 2px 4px rgba(0,0,0,0.6)",
          padding: "0 20px",
        }}
      >
        <h2 style={{ fontSize: 32, marginBottom: 10 }}>{currentSlide.title}</h2>
        {currentSlide.description ? (
          <p style={{ fontSize: 18, maxWidth: 600, margin: "0 auto" }}>
            {currentSlide.description}
          </p>
        ) : null}
      </motion.div>

      {/* تصاویر Overlay */}
      {showOverlays && (
        <>
          {/* چپ */}
          <motion.img
            key={`${currentSlide.overlays[0].src}-${currentIndex}-L`}
            src={currentSlide.overlays[0].src}
            alt={currentSlide.overlays[0].alt}
            className={currentSlide.overlays[0].className}
            initial={{ x: isRTL ? 100 : -100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            style={{
              position: "absolute",
              transform: "translate(-50%, -50%)",
              height: "auto",
              zIndex: 2,
              borderRadius: 12,
              objectFit: "contain",
              backgroundColor: "rgba(0,0,0,0.5)",
              backdropFilter: "blur(1px)",
            }}
          />

          {/* وسط (پیکان) - اگر کلاسش استایل‌دهی می‌کند، نیازی به انیمیشن نیست */}
          <motion.img
            key={`${currentSlide.overlays[1].src}-${currentIndex}-C`}
            src={currentSlide.overlays[1].src}
            alt={currentSlide.overlays[1].alt}
            className={currentSlide.overlays[1].className}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.5 }}
          />

          {/* راست */}
          <motion.img
            key={`${currentSlide.overlays[2].src}-${currentIndex}-R`}
            src={currentSlide.overlays[2].src}
            alt={currentSlide.overlays[2].alt}
            className={currentSlide.overlays[2].className}
            initial={{ x: isRTL ? -100 : 100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.6 }}
            style={{
              position: "absolute",
              transform: "translate(-50%, -50%)",
              height: "auto",
              zIndex: 2,
              borderRadius: 12,
              objectFit: "contain",
              backgroundColor: "rgba(0,0,0,0.5)",
              backdropFilter: "blur(1px)",
            }}
          />
        </>
      )}
    </div>
  );
}
